(() => {
  'use strict';
  if (customElements.get('arya-load-more')) return;
  class AryaLoadMore extends HTMLElement {
    connectedCallback() {
      queueMicrotask(() => { if (this.isConnected) this.initialize(); });
    }
    initialize() {
      this.grid = this.closest('#ProductGridContainer')?.querySelector('#product-grid');
      this.button = this.querySelector('[data-load-more]');
      this.status = this.querySelector('[data-load-status]');
      if (!this.grid || !this.button || !this.status) return;
      this.ar = document.documentElement.lang.startsWith('ar');
      this.label = this.ar ? 'تحميل المزيد' : 'Load more';
      this.startUrl = location.pathname + location.search;
      this.loaded = new Set();
      const selectedAvailability = new URL(location.href).searchParams.getAll('filter.v.availability');
      this.unavailableOnly = selectedAvailability.includes('0') && !selectedAvailability.includes('1') &&
        [...this.grid.children].every(item => item.dataset.avAvailable === 'false');
      this.pendingUnavailable = [];
      if (this.dataset.next && !this.unavailableOnly) {
        for (const item of [...this.grid.children]) {
          if (item.dataset.avAvailable === 'false') {
            this.pendingUnavailable.push(item);
            item.remove();
          }
        }
      }
      this.querySelector('[data-load-fallback]').hidden = true;
      this.button.hidden = !this.dataset.next;
      this.status.hidden = false;
      this.button.onclick = () => this.load(true);
      this.updateStatus();
      if (this.dataset.next && 'IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => {
          if (entries.some(entry => entry.isIntersecting)) this.load(false);
        }, { rootMargin: '0px 0px 200px 0px' });
        this.observer.observe(this);
      }
    }
    disconnectedCallback() {
      this.observer?.disconnect();
      this.controller?.abort();
    }
    updateStatus() {
      const count = this.grid.children.length;
      const total = Number(this.dataset.total);
      this.status.textContent = this.ar ? `عرض ${count} من ${total} منتج` : `Showing ${count} of ${total} products`;
    }
    async load(manual) {
      if (this.busy || !this.dataset.next || !this.isConnected || (!manual && this.failed)) return;
      if (location.pathname + location.search !== this.startUrl) return;
      const url = new URL(this.dataset.next, location.href);
      if (url.origin !== location.origin || this.loaded.has(url.href)) return;
      this.busy = true;
      this.failed = false;
      this.button.disabled = true;
      this.button.textContent = this.ar ? 'جارٍ التحميل…' : 'Loading…';
      this.grid.setAttribute('aria-busy', 'true');
      this.controller = new AbortController();
      const timer = setTimeout(() => this.controller.abort(), 15000);
      try {
        const requestUrl = new URL(url);
        requestUrl.searchParams.set('section_id', this.dataset.section);
        const preview = new URL(location.href).searchParams.get('preview_theme_id');
        if (preview) requestUrl.searchParams.set('preview_theme_id', preview);
        const response = await fetch(requestUrl, { signal: this.controller.signal, credentials: 'same-origin' });
        if (!response.ok) throw new Error('Collection page unavailable');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const nextGrid = html.querySelector('#product-grid');
        const nextControl = html.querySelector('arya-load-more');
        if (!nextGrid || nextGrid.dataset.id !== this.dataset.section) throw new Error('Collection response mismatch');
        if (!this.isConnected || location.pathname + location.search !== this.startUrl) return;
        if (this.unavailableOnly && [...nextGrid.children].some(item => item.dataset.avAvailable === 'true')) {
          this.unavailableOnly = false;
          for (const item of [...this.grid.children]) {
            if (item.dataset.avAvailable === 'false') {
              this.pendingUnavailable.push(item);
              item.remove();
            }
          }
        }
        const existing = new Set([...this.grid.children, ...this.pendingUnavailable].map(item => item.dataset.avProductId));
        const fragment = document.createDocumentFragment();
        let firstNew;
        for (const item of [...nextGrid.children]) {
          if (!item.dataset.avProductId || existing.has(item.dataset.avProductId)) continue;
          existing.add(item.dataset.avProductId);
          item.classList.add('scroll-trigger--cancel');
          item.querySelectorAll('.scroll-trigger').forEach(element => element.classList.add('scroll-trigger--cancel'));
          const copy = document.importNode(item, true);
          if (!this.unavailableOnly && copy.dataset.avAvailable === 'false') {
            this.pendingUnavailable.push(copy);
          } else {
            firstNew ||= copy.querySelector('.card__heading a');
            fragment.append(copy);
          }
        }
        const firstVisibleUnavailable = [...this.grid.children].find(item => item.dataset.avAvailable === 'false');
        this.grid.insertBefore(fragment, firstVisibleUnavailable || null);
        this.loaded.add(url.href);
        this.dataset.next = nextControl?.dataset.next || '';
        if (!this.dataset.next && this.pendingUnavailable.length) {
          const pending = document.createDocumentFragment();
          for (const item of this.pendingUnavailable) {
            firstNew ||= item.querySelector('.card__heading a');
            pending.append(item);
          }
          this.grid.append(pending);
          this.pendingUnavailable = [];
        }
        this.updateStatus();
        if (manual) firstNew?.focus({ preventScroll: true });
        if (!this.dataset.next) {
          this.observer?.disconnect();
          this.button.hidden = true;
        } else if (this.observer) {
          this.observer.unobserve(this);
          requestAnimationFrame(() => { if (this.isConnected) this.observer.observe(this); });
        }
        document.dispatchEvent(new CustomEvent('arya:products-loaded', { detail: { count: this.grid.children.length } }));
      } catch (error) {
        if (!this.isConnected) return;
        if (this.pendingUnavailable.length) {
          this.grid.append(...this.pendingUnavailable);
          this.pendingUnavailable = [];
        }
        this.failed = true;
        this.status.textContent = this.ar ? 'تعذّر تحميل المنتجات. حاول مرة أخرى.' : 'Products could not load. Please try again.';
        this.button.textContent = this.ar ? 'إعادة المحاولة' : 'Try again';
      } finally {
        clearTimeout(timer);
        this.busy = false;
        this.grid.removeAttribute('aria-busy');
        this.button.disabled = false;
        if (!this.failed) this.button.textContent = this.label;
      }
    }
  }
  customElements.define('arya-load-more', AryaLoadMore);
})();
